import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, FileText, Loader2, AlertTriangle } from 'lucide-react';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useEntitlements } from '../../hooks/useEntitlements';
import { usePaystackPayment } from 'react-paystack';
import toast, { Toaster } from 'react-hot-toast';
import type { Product } from '../../types';

export default function ProductDetail() {
  const { productId } = useParams<{ productId: string }>();
  const { user } = useAuth();
  const { hasAccess, loading: entitlementsLoading } = useEntitlements();
  const navigate = useNavigate();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [pollingForEntitlement, setPollingForEntitlement] = useState(false);

  const owned = hasAccess(productId || '');

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) return;

      try {
        const productDoc = await getDoc(doc(db, 'products', productId));
        if (productDoc.exists()) {
          setProduct({ id: productDoc.id, ...productDoc.data() } as Product);
        } else {
          toast.error('Product not found');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  // Poll for entitlement after payment
  const pollForEntitlement = (productId: string, maxAttempts = 15) => {
    setPollingForEntitlement(true);
    let attempts = 0;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'entitlements'),
        where('email', '==', user?.email?.toLowerCase().trim()),
        where('productId', '==', productId)
      ),
      (snapshot) => {
        if (!snapshot.empty) {
          // Entitlement found!
          unsubscribe();
          setPollingForEntitlement(false);
          toast.success('Purchase complete! Redirecting to downloads...');
          setTimeout(() => navigate('/portal/downloads'), 2000);
        } else {
          attempts++;
          if (attempts >= maxAttempts) {
            unsubscribe();
            setPollingForEntitlement(false);
            toast.error('Purchase may still be processing. Check My Downloads in a few moments.');
          }
        }
      },
      (error) => {
        console.error('Error polling for entitlement:', error);
        unsubscribe();
        setPollingForEntitlement(false);
      }
    );

    // Cleanup after max time (30 seconds)
    setTimeout(() => {
      unsubscribe();
      setPollingForEntitlement(false);
    }, 30000);
  };

  // Paystack configuration
  const config = {
    email: user?.email || '',
    amount: (product?.priceZAR || 0) * 100, // Convert to kobo (cents)
    publicKey: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || '',
    metadata: {
      productId: product?.id || '',
      userId: user?.uid || '',
      custom_fields: [
        {
          display_name: 'Product',
          variable_name: 'product',
          value: product?.name || ''
        }
      ]
    }
  };

  const initializePayment = usePaystackPayment(config);

  const handleSuccess = () => {
    setProcessing(true);
    toast.success('Payment successful! Processing your purchase...');

    // Start polling for entitlement
    if (product?.id) {
      pollForEntitlement(product.id);
    }
  };

  const handleClose = () => {
    toast.error('Payment cancelled');
  };

  const handlePayment = () => {
    if (!user?.email) {
      toast.error('Please sign in to purchase');
      return;
    }

    if (!product) {
      toast.error('Product not loaded');
      return;
    }

    initializePayment({ onSuccess: handleSuccess, onClose: handleClose });
  };

  if (loading || entitlementsLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h2>
        <Link to="/portal/products" className="text-blue-600 hover:text-blue-700">
          ← Back to products
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Toaster position="top-center" />

      {/* Back button */}
      <Link
        to="/portal/products"
        className="inline-flex items-center text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to products
      </Link>

      {/* Product header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{product.name}</h1>
            <p className="text-3xl font-semibold text-blue-600">
              R{product.priceZAR.toLocaleString()}
            </p>
          </div>
          {owned && (
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              You own this
            </span>
          )}
        </div>

        {product.description && (
          <p className="text-lg text-gray-700">{product.description}</p>
        )}
      </div>

      {/* Processing banner */}
      {(processing || pollingForEntitlement) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <Loader2 className="animate-spin h-5 w-5 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Processing your purchase...
              </p>
              <p className="text-xs text-blue-700 mt-1">
                This may take a few moments. Please don't close this page.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* What's included */}
          {product.features && product.features.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">What's Included</h2>
              <ul className="space-y-3">
                {product.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Files list */}
          {product.files && product.files.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Available Files ({product.files.length})
              </h2>
              <div className="space-y-2">
                {product.files.map((file) => (
                  <div
                    key={file.key}
                    className="flex items-center p-3 bg-gray-50 rounded-md"
                  >
                    <FileText className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-sm text-gray-700">{file.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Purchase card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 sticky top-6">
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">Price</p>
              <p className="text-3xl font-bold text-gray-900">
                R{product.priceZAR.toLocaleString()}
              </p>
            </div>

            {owned ? (
              <>
                <Link
                  to="/portal/downloads"
                  className="block w-full text-center px-6 py-3 rounded-md font-medium bg-green-600 text-white hover:bg-green-700 transition-colors mb-4"
                >
                  View Your Downloads
                </Link>
                <p className="text-sm text-gray-600 text-center">
                  You already own this product
                </p>
              </>
            ) : (
              <>
                <button
                  onClick={handlePayment}
                  disabled={processing || pollingForEntitlement}
                  className="w-full px-6 py-3 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-4"
                >
                  {processing || pollingForEntitlement ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="animate-spin h-5 w-5 mr-2" />
                      Processing...
                    </span>
                  ) : (
                    `Buy Now - R${product.priceZAR.toLocaleString()}`
                  )}
                </button>

                <div className="space-y-2 text-xs text-gray-600">
                  <p className="flex items-center">
                    <CheckCircle2 className="h-3 w-3 mr-2 text-green-500" />
                    Instant access after payment
                  </p>
                  <p className="flex items-center">
                    <CheckCircle2 className="h-3 w-3 mr-2 text-green-500" />
                    Secure payment via Paystack
                  </p>
                  <p className="flex items-center">
                    <CheckCircle2 className="h-3 w-3 mr-2 text-green-500" />
                    Lifetime access to updates
                  </p>
                </div>
              </>
            )}

            {/* Help text */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                Need help? Email us at support@dealvelocity.co.za
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
